import { Fragment, memo, ReactElement, useCallback } from 'react';
import { useAtom } from 'jotai';
import { App, Button, Switch, TimePicker } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { BranchCode, Weekday } from '../types';
import { WEEKDAYS, WEEKDAY_LABEL } from '../types';
import { scheduleAtomFamily } from '../atoms/settingAtoms';
import { findInvalidScheduleEntries } from '../utils/findInvalidScheduleEntries';
import {
  ScheduleGrid,
  WeekdayLabelCell,
  TimeField,
  ScheduleFooter,
  SectionHeading,
  ToggleSection,
  ToggleNote,
  ToggleRow,
  ToggleLabel,
} from './styles';
import TimeRangeSeparator from './TimeRangeSeparator';

interface Props {
  branchCode: BranchCode;
}

// ブラウザ既定の時刻入力は表示形式がロケール依存（AM/PM になる）ため、24時間表記を固定できる
// TimePicker を使う。TimePicker は Dayjs を要求するが設定値は時刻のみのため、日付は基準日で埋める。
const TIME_FORMAT = 'HH:mm';
const TIME_ANCHOR_DATE = '2000-01-01';

const toDayjs = (value: string | null): Dayjs | null => (value === null ? null : dayjs(`${TIME_ANCHOR_DATE}T${value}`));

const ScheduleEditor = memo(function ScheduleEditor({ branchCode }: Props): ReactElement {
  const { message } = App.useApp();
  const [state, setState] = useAtom(scheduleAtomFamily(branchCode));
  const { current, baseline } = state;

  const handleChange = useCallback(
    (day: Weekday, edge: 'from' | 'to') => (time: Dayjs | null) => {
      const next = time === null ? null : time.format(TIME_FORMAT);
      setState(prev => ({
        ...prev,
        current: {
          ...prev.current,
          weekdays: {
            ...prev.current.weekdays,
            [day]: { ...prev.current.weekdays[day], [edge]: next },
          },
        },
      }));
    },
    [setState]
  );

  const handleToggleHoliday = useCallback(
    (checked: boolean) => {
      setState(prev => ({ ...prev, current: { ...prev.current, callOnHoliday: checked } }));
    },
    [setState]
  );

  const handleToggleYearEnd = useCallback(
    (checked: boolean) => {
      setState(prev => ({ ...prev, current: { ...prev.current, callOnYearEnd: checked } }));
    },
    [setState]
  );

  const handleSave = useCallback(() => {
    const invalid = findInvalidScheduleEntries(current.weekdays);
    if (invalid.length > 0) {
      const details = invalid
        .map(({ day, missingEdge }) => `${WEEKDAY_LABEL[day]}曜日の${missingEdge === 'from' ? '開始時刻' : '終了時刻'}`)
        .join('、');
      void message.error(`${details}が未入力です`);
      return;
    }
    // MOCK [#239]: 本番実装時はバックエンドへの保存リクエストに置き換え、成功時に baseline を current に同期する。
    setState(prev => ({ ...prev, baseline: prev.current }));
    void message.success('自動架電のスケジュールを保存しました');
  }, [current, setState, message]);

  return (
    <>
      <SectionHeading>自動架電対象の時間帯</SectionHeading>
      <ScheduleGrid>
        {WEEKDAYS.map(day => {
          const range = current.weekdays[day];
          const baselineRange = baseline.weekdays[day];
          return (
            <Fragment key={day}>
              <WeekdayLabelCell>{WEEKDAY_LABEL[day]}</WeekdayLabelCell>
              <TimeField $dirty={range.from !== baselineRange.from}>
                <TimePicker
                  format={TIME_FORMAT}
                  needConfirm={false}
                  showNow={false}
                  placeholder='--:--'
                  value={toDayjs(range.from)}
                  onChange={handleChange(day, 'from')}
                  aria-label={`${WEEKDAY_LABEL[day]} 開始時刻`}
                />
              </TimeField>
              <TimeRangeSeparator />
              <TimeField $dirty={range.to !== baselineRange.to}>
                <TimePicker
                  format={TIME_FORMAT}
                  needConfirm={false}
                  showNow={false}
                  placeholder='--:--'
                  value={toDayjs(range.to)}
                  onChange={handleChange(day, 'to')}
                  aria-label={`${WEEKDAY_LABEL[day]} 終了時刻`}
                />
              </TimeField>
            </Fragment>
          );
        })}
      </ScheduleGrid>
      <ToggleSection>
        <ToggleNote>祝日・年末年始に架電する場合の時間帯は、その日の曜日の設定に従います。</ToggleNote>
        <ToggleRow>
          <ToggleLabel>祝日に架電する</ToggleLabel>
          <Switch checked={current.callOnHoliday} onChange={handleToggleHoliday} aria-label='祝日に架電する' />
        </ToggleRow>
        <ToggleRow>
          <ToggleLabel>年末年始（12/29〜1/3）に架電する</ToggleLabel>
          <Switch checked={current.callOnYearEnd} onChange={handleToggleYearEnd} aria-label='年末年始に架電する' />
        </ToggleRow>
      </ToggleSection>
      <ScheduleFooter>
        <Button type='primary' icon={<SaveOutlined />} onClick={handleSave}>
          保存
        </Button>
      </ScheduleFooter>
    </>
  );
});

export default ScheduleEditor;
